import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Crown, UserPlus, Link as LinkIcon, ArrowsClockwise, Power, Prohibit, Copy,
} from "@phosphor-icons/react";

/**
 * /admin/live-hosts — Admin panel for NKognito Live host onboarding.
 * Phase 1: invite a host, view the private setup link, list all hosts,
 * enable/disable them, reissue an expired invite.
 */
export default function LiveHosts() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/live/admin/hosts");
      setHosts(data.hosts || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load hosts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2">
            <Crown size={12} weight="fill" /> NKognito Live
          </p>
          <h1 className="font-display text-2xl mt-1">Hosts</h1>
          <p className="text-xs text-nk-mute mt-1 max-w-xl">
            Invite hosts by email. Copy the private setup link and deliver it manually
            (WhatsApp/email). The host completes setup, then logs in from{" "}
            <code className="text-nk-gold">/host/login</code>.
          </p>
        </div>
        <button
          onClick={() => setInviteOpen(true)}
          data-testid="live-hosts-invite-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-sm bg-nk-gold text-nk-bg hover:opacity-90"
        >
          <UserPlus size={14} weight="bold" /> Invite host
        </button>
      </header>

      {lastInvite && (
        <SetupLinkBanner invite={lastInvite} onDismiss={() => setLastInvite(null)} />
      )}

      <section className="nk-card p-0 overflow-hidden" data-testid="live-hosts-list">
        <div className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_auto] gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-nk-dim border-b border-nk-line/50">
          <span>Host</span>
          <span>Email · Org</span>
          <span>Status</span>
          <span>Invited</span>
          <span>Actions</span>
        </div>
        {loading ? (
          <p className="p-6 text-sm text-nk-mute text-center">Loading…</p>
        ) : hosts.length === 0 ? (
          <p className="p-6 text-sm text-nk-mute text-center">No hosts yet — invite your first one above.</p>
        ) : (
          hosts.map((h) => (
            <HostRow key={h.host_id} host={h} onChanged={load} onNewInvite={setLastInvite} />
          ))
        )}
      </section>

      {inviteOpen && (
        <InviteHostModal
          onClose={() => setInviteOpen(false)}
          onSuccess={(res) => { setLastInvite(res); setInviteOpen(false); load(); }}
        />
      )}
    </div>
  );
}


function HostRow({ host, onChanged, onNewInvite }) {
  const [busy, setBusy] = useState(false);
  const statusPill = useMemo(() => {
    if (host.status === "active")   return "text-nk-accent border-nk-accent/60";
    if (host.status === "disabled") return "text-nk-bad border-nk-bad/60";
    return "text-nk-gold border-nk-gold/60";  // invited
  }, [host.status]);

  const toggleStatus = async () => {
    const next = host.status === "disabled" ? "active" : "disabled";
    setBusy(true);
    try {
      await api.post(`/live/admin/hosts/${host.host_id}/status`, { status: next });
      toast.success(`Host ${next}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reissueInvite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/live/admin/hosts/${host.host_id}/reissue-invite`);
      onNewInvite({ ...data, email: host.email, name: host.name });
      toast.success("New setup link generated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const invited = host.invited_at ? new Date(host.invited_at).toLocaleDateString() : "—";

  return (
    <div
      className="grid grid-cols-[1.5fr_1.5fr_1fr_1fr_auto] gap-3 px-4 py-3 border-b border-nk-line/30 items-center text-sm hover:bg-nk-charcoal/30"
      data-testid={`live-host-row-${host.host_id}`}
    >
      <div>
        <p className="font-medium text-nk-text">{host.name}</p>
        <p className="text-[10px] text-nk-dim font-mono">{host.event_type || "—"}</p>
      </div>
      <div>
        <p className="text-nk-text truncate">{host.email}</p>
        <p className="text-[10px] text-nk-dim truncate">{host.org || "—"}</p>
      </div>
      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm w-fit ${statusPill}`}>
        {host.status}
      </span>
      <span className="text-xs text-nk-mute">{invited}</span>
      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={reissueInvite} disabled={busy}
          data-testid={`live-host-reissue-${host.host_id}`}
          title="Regenerate setup link"
          className="p-1.5 text-nk-mute hover:text-nk-gold transition-colors disabled:opacity-40"
        >
          <ArrowsClockwise size={14} />
        </button>
        <button
          onClick={toggleStatus} disabled={busy}
          data-testid={`live-host-toggle-${host.host_id}`}
          title={host.status === "disabled" ? "Enable host" : "Disable host"}
          className={`p-1.5 transition-colors disabled:opacity-40 ${
            host.status === "disabled" ? "text-nk-mute hover:text-nk-accent" : "text-nk-mute hover:text-nk-bad"
          }`}
        >
          {host.status === "disabled" ? <Power size={14} /> : <Prohibit size={14} />}
        </button>
      </div>
    </div>
  );
}


function InviteHostModal({ onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [eventType, setEventType] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/live/admin/hosts/invite", {
        email, name, org: org || null,
        event_type: eventType || null, notes: notes || null,
      });
      onSuccess({ ...data, email, name });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Failed to invite"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md nk-card p-6 space-y-3"
        data-testid="invite-host-modal"
      >
        <p className="eyebrow">Invite a host</p>
        <input
          data-testid="invite-host-name"
          required minLength={2}
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Host name"
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        />
        <input
          data-testid="invite-host-email"
          required type="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="host@example.com"
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        />
        <input
          data-testid="invite-host-org"
          value={org} onChange={(e) => setOrg(e.target.value)}
          placeholder="Organization (optional)"
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        />
        <select
          data-testid="invite-host-event-type"
          value={eventType} onChange={(e) => setEventType(e.target.value)}
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        >
          <option value="">Event type (optional)</option>
          <option value="corporate">Corporate</option>
          <option value="school">School / campus</option>
          <option value="bar">Bar / pub</option>
          <option value="community">Community</option>
          <option value="wedding">Wedding / party</option>
          <option value="other">Other</option>
        </select>
        <textarea
          data-testid="invite-host-notes"
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Internal notes (optional) — e.g. how you know them"
          className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
        />
        <div className="flex items-center gap-2 pt-2">
          <button
            type="button" onClick={onClose}
            className="flex-1 px-4 py-2 text-sm rounded-sm border border-nk-line text-nk-mute hover:text-nk-text"
          >
            Cancel
          </button>
          <button
            type="submit" disabled={busy}
            data-testid="invite-host-submit"
            className="flex-1 px-4 py-2 text-sm rounded-sm bg-nk-gold text-nk-bg hover:opacity-90 disabled:opacity-40"
          >
            {busy ? "Inviting…" : "Generate link"}
          </button>
        </div>
      </form>
    </div>
  );
}


function SetupLinkBanner({ invite, onDismiss }) {
  const setupUrl = `${window.location.origin}${invite.setup_path}`;
  const copy = () => {
    navigator.clipboard.writeText(setupUrl).then(
      () => toast.success("Setup link copied"),
      () => toast.error("Copy failed — long-press the link"),
    );
  };
  return (
    <div className="nk-card p-4 border border-nk-gold/60 bg-nk-gold/[0.06] space-y-2" data-testid="setup-link-banner">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <LinkIcon size={16} weight="bold" className="text-nk-gold mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-nk-gold font-medium">
              Setup link generated for {invite.name || invite.email}
            </p>
            <p className="text-[11px] text-nk-mute mt-1">
              Deliver this link privately (WhatsApp / email). It expires{" "}
              {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "in 30 days"}.
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-xs text-nk-mute hover:text-nk-text"
        >
          Dismiss
        </button>
      </div>
      <div className="flex items-center gap-2 bg-nk-bg/60 px-3 py-2 rounded-sm border border-nk-line">
        <code className="text-[11px] font-mono text-nk-text flex-1 truncate">{setupUrl}</code>
        <button
          onClick={copy}
          data-testid="setup-link-copy"
          className="inline-flex items-center gap-1 text-xs text-nk-gold hover:opacity-80"
        >
          <Copy size={12} /> Copy
        </button>
      </div>
    </div>
  );
}
